import {
  Card,
  Col,
  Descriptions,
  DescriptionsProps,
  Row,
  Segmented,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { INBOX_DARK_BACKGROUND } from 'features/inbox/colors'
import {
  ASSET_COLORS,
  getColorForAsset,
} from 'features/inbox/components/inspectionCards/utils'
import { InspectionAssetViz } from 'features/inbox/components/inspectionDetails/InspectionAssetViz'
import { InspectionFfsResultsTable } from 'features/inbox/components/inspectionDetails/InspectionFfsResultsTable'
import { InspectionIndicationsTable } from 'features/inbox/components/inspectionDetails/InspectionIndicationsTable'
import { InspectionOverviewContent } from 'features/inbox/components/shared/InspectionOverviewContent'
import {
  EnrichedGeckoInspection,
  EnrichedGeckoInspectionData,
} from 'features/inbox/types/types'
import { INSPECTION_DATE_FORMAT } from 'features/inbox/utils/date'
import { IndicationItem } from 'features/indicationTriage'
import React, { FC, memo, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataset,
  GeckoEntry,
  GeckoJob,
  GeckoUnit,
} from 'types'

type VisualizationMode = '2D' | '3D'

const SegmentedWrapper = styled.div`
  .ant-segmented-item {
    transition: all 0.2s ease;
  }

  .ant-segmented-item-selected {
    font-weight: 600;
  }
`

const CardTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const { Text } = Typography

const StyledCard = styled(Card)`
  background: ${INBOX_DARK_BACKGROUND};
  border: none;
  margin-bottom: 1rem;

  .ant-card-head {
    border-bottom: none;
  }
`

const StyledDescriptions = styled(Descriptions)`
  .ant-descriptions-item-label {
    color: ${CantileverTheme.colors.grayscale[400]} !important;
    font-size: 0.75rem;
    padding-bottom: 0.25rem !important;
  }

  .ant-descriptions-item-content {
    color: ${CantileverTheme.colors.grayscale[100]};
    font-weight: 500;
  }

  .ant-descriptions-row > td {
    padding-bottom: 0.75rem !important;
  }
`

interface InspectionDetailsPageProps {
  inspection?: EnrichedGeckoInspection
  job?: GeckoJob
  component?: GeckoComponent
  unit?: GeckoUnit
  componentDataset?: GeckoComponentDataset
  assetModel?: GeckoAssetModel
  indications?: readonly IndicationItem[]
  ffsReports?: readonly GeckoEntry[]
}

const vizModeOptions: { label: string; value: VisualizationMode }[] = [
  { label: '2D', value: '2D' },
  { label: '3D', value: '3D' },
]

const InspectionDetailsPageComponent: FC<InspectionDetailsPageProps> = ({
  inspection,
  component,
  assetModel,
  indications = [],
  ffsReports = [],
}) => {
  const [vizMode, setVizMode] = useState<VisualizationMode>('3D')

  const inspectionData: EnrichedGeckoInspectionData | undefined =
    useMemo(() => {
      return inspection?.data
    }, [inspection])

  const unitColor = useMemo(() => {
    if (!inspection?.tags.unit) return ASSET_COLORS[0]
    return getColorForAsset(inspection.tags.unit)
  }, [inspection?.tags.unit])

  const isRautInspection = useMemo(() => {
    return inspectionData?.inspection_type?.toLowerCase() === 'raut'
  }, [inspectionData])

  const descriptionItems: DescriptionsProps['items'] = useMemo(
    () =>
      [
        { label: 'Unit', value: inspectionData?.unitName },
        { label: 'Component', value: inspectionData?.componentName },
        { label: 'Job', value: inspectionData?.jobName },
        { label: 'Vendor', value: inspectionData?.vendor },
        {
          label: 'Inspection Date',
          value: inspectionData?.date
            ? dayjs(inspectionData?.date).format(INSPECTION_DATE_FORMAT)
            : undefined,
        },
        { label: 'X Bin Size', value: String(inspectionData?.x_bin_size) },
        { label: 'Y Bin Size', value: String(inspectionData?.y_bin_size) },
      ].map((item) => ({
        key: item.label,
        label: item.label,
        children: item.value || '-',
      })),
    [inspectionData]
  )

  const detailsTabContent = useMemo(() => {
    if (!inspection || !inspectionData) {
      return null
    }

    return (
      <div>
        {/* Overview Card */}
        <StyledCard style={{ marginBottom: '1rem' }}>
          <InspectionOverviewContent
            inspection={inspection}
            unitColor={unitColor}
          />
        </StyledCard>

        <Row gutter={[16, 16]}>
          {/* Left Column */}
          <Col xs={24} lg={10}>
            <StyledCard
              title='Details'
              variant='borderless'
              extra={<Tag color='blue'>ID: {inspection?.id}</Tag>}>
              <StyledDescriptions
                column={{ xs: 1, sm: 2, md: 2, lg: 2 }}
                layout='vertical'
                colon={false}
                size='small'
                items={descriptionItems}
              />
            </StyledCard>

            {/* Indications Section */}
            <StyledCard title='Indications' variant='borderless'>
              <InspectionIndicationsTable
                indications={indications}
                componentId={component?.id}
              />
            </StyledCard>
          </Col>

          {/* Right Column */}
          <Col xs={24} lg={14}>
            {inspection && (
              <StyledCard
                title={
                  <CardTitleWrapper>
                    <span>Visualization</span>
                    <SegmentedWrapper>
                      <Segmented<VisualizationMode>
                        size='small'
                        value={vizMode}
                        onChange={setVizMode}
                        options={vizModeOptions}
                      />
                    </SegmentedWrapper>
                  </CardTitleWrapper>
                }
                variant='borderless'
                style={{ marginBottom: '1rem' }}
                styles={{
                  body: { padding: 0 },
                }}>
                <InspectionAssetViz
                  inspection={inspection}
                  component={component}
                  assetModel={assetModel}
                  threeDimensionsEnabled={vizMode === '3D'}
                />
              </StyledCard>
            )}
          </Col>
        </Row>
      </div>
    )
  }, [
    inspection,
    inspectionData,
    unitColor,
    descriptionItems,
    vizMode,
    component,
    assetModel,
    indications,
  ])

  // Memoize FFS Results tab content separately to prevent re-renders
  const ffsResultsTabContent = useMemo(() => {
    return (
      <div>
        <StyledCard title='FFS Results' variant='borderless'>
          <InspectionFfsResultsTable
            indications={indications}
            ffsReports={ffsReports}
            componentId={component?.id}
          />
        </StyledCard>
      </div>
    )
  }, [indications, ffsReports, component])

  // Build tabs items
  const tabItems = useMemo(() => {
    const items = [
      {
        key: 'details',
        label: 'Details',
        children: detailsTabContent,
      },
    ]

    // Add FFS Results tab only for RAUT inspections
    if (isRautInspection) {
      items.push({
        key: 'ffs-results',
        label: 'FFS Results',
        children: ffsResultsTabContent,
      })
    }

    return items
  }, [isRautInspection, detailsTabContent, ffsResultsTabContent])

  if (!inspection || inspectionData === undefined) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Text type='secondary'>No inspection selected</Text>
      </div>
    )
  }

  return (
    <Tabs
      defaultActiveKey='details'
      items={tabItems}
      destroyInactiveTabPane={true}
    />
  )
}

export const InspectionDetailsPage = memo(InspectionDetailsPageComponent)
